import React from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { Ban, Calendar, ChevronDown, ChevronRight, Eye } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ProjectSelector } from '@/components/ProjectSelector';
import { CostCentreSelector } from '@/components/CostCentreSelector';
import { RoleSelector } from '@/components/RoleSelector';
import { AbsenceIndicator } from '@/components/AbsenceBadge';
import { OvertimeTypeBadge } from '@/components/timesheet/OvertimeTypeBadge';
import { RowHoursToCompleteCell } from '@/components/timesheet/row/RowHoursToCompleteCell';
import { useBudgetContext } from '@/hooks/useBudgetContext';
import { calculateEntryVariance, getVarianceBadgeVariant } from '@/services/varianceCalculationService';
import { cn } from '@/lib/utils';
import type { CostCentre, DayKey, Project, TimesheetEntry } from '@/types/timesheet';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';
import type { ApprovalDay } from '@/utils/ApprovalDashboard/dayHelpers';
import { isWeekendDay } from '@/utils/ApprovalDashboard/dayHelpers';
import type { EditedEntries, SelectionLabel } from '@/hooks/ApprovalDashboard/useTimesheetReviewEdit';
import { useFeatureFlags } from '@/contexts/FeatureFlagContext';

export type BookingDetails = Omit<TimesheetEntry, 'scheduledHours'> & {
  projectName: string;
  costCentreName: string;
  scheduledHours: NonNullable<TimesheetEntry['scheduledHours']>;
};

const NO_SCHEDULED_HOURS: NonNullable<TimesheetEntry['scheduledHours']> = {
  monday: 0, tuesday: 0, wednesday: 0, thursday: 0, friday: 0, saturday: 0, sunday: 0,
};

export interface TimesheetReviewRowProps {
  entry: TimesheetEntry;
  entryEdits: EditedEntries[number];
  days: ApprovalDay[];
  isEditMode: boolean;
  isDaily: boolean;
  orgSettings: OrganisationTimesheetSettings;
  overtimeEnabled: boolean;
  overtimeTypes: Array<{ id: number; name: string; badgeColour: string }>;
  effectiveProjects: Project[];
  selectableProjects: Project[];
  /** Cost centres valid for a project, keyed by project code. */
  costCentresByProjectCode: Record<string, CostCentre[]>;
  currentCompanyId?: string;
  /** Employee the reviewed timesheet belongs to — used for budget context lookups. */
  employeeId?: number;
  screenMode: 'demo' | 'live';
  isExpanded: boolean;
  editingHours: Record<string, string | undefined>;
  setEditingHours: Dispatch<SetStateAction<Record<string, string | undefined>>>;
  getHoursCellKey: (entryId: number, day: ApprovalDay) => string;
  toggleEntryExpansion: (entryId: number) => void;
  shouldShowExpansionIcon: (description: string) => boolean;
  handleEntryFieldChange: (entryId: number, field: 'projectId' | 'costCentreId' | 'description', value: string) => void;
  handleEntryProjectChange: (entryId: number, projectId: string, project?: SelectionLabel) => void;
  handleEntryCostCentreChange: (entryId: number, costCentreId: string, costCentre?: SelectionLabel) => void;
  handleEntryRoleChange: (entryId: number, roleId: number | undefined, roleName?: string) => void;
  handleEntryHoursChange: (entryId: number, day: DayKey, value: string) => void;
  handleDailyNoteChange: (entryId: number, day: DayKey, value: string) => void;
  onViewBookingDetails: (booking: BookingDetails) => void;
}

export function TimesheetReviewRow({
  entry,
  entryEdits,
  days,
  isEditMode,
  isDaily,
  orgSettings,
  overtimeEnabled,
  overtimeTypes,
  effectiveProjects,
  selectableProjects,
  costCentresByProjectCode,
  currentCompanyId,
  employeeId,
  screenMode,
  isExpanded,
  editingHours,
  setEditingHours,
  getHoursCellKey,
  toggleEntryExpansion,
  shouldShowExpansionIcon,
  handleEntryFieldChange,
  handleEntryProjectChange,
  handleEntryCostCentreChange,
  handleEntryRoleChange,
  handleEntryHoursChange,
  handleDailyNoteChange,
  onViewBookingDetails,
}: TimesheetReviewRowProps) {
  // Merge edits over original values for display
  const effectiveProjectId = entryEdits.projectId ?? entry.projectId;
  const effectiveCostCentreId = entryEdits.costCentreId ?? entry.costCentreId;
  const effectiveDescription = entryEdits.description ?? entry.description;
  const effectiveRoleId = 'roleId' in entryEdits ? entryEdits.roleId : entry.roleId;
  const effectiveRoleName = 'roleName' in entryEdits ? entryEdits.roleName : entry.roleName;
  const effectiveHours = entryEdits.hours ?? entry.hours;

  const project = effectiveProjects.find(p => p.id === effectiveProjectId);
  // Only offer cost centres belonging to the selected project; the
  // queue-wide list on `project.costCentres` covers every project.
  const projectCode = project?.code
    ?? (effectiveProjectId === entry.projectId ? entry.projectCode : undefined);
  const entryCostCentres = screenMode === 'live'
    ? (projectCode ? costCentresByProjectCode[projectCode] ?? [] : [])
    : project?.costCentres ?? [];
  const costCentre = (entryCostCentres.length > 0 ? entryCostCentres : project?.costCentres ?? [])
    .find(cc => cc.id === effectiveCostCentreId);
  // Keep the entry's current project selectable even once it is closed
  const projectOptions = project && !selectableProjects.includes(project)
    ? [project, ...selectableProjects]
    : selectableProjects;
  const total = Object.values(effectiveHours).reduce((sum, h) => sum + h, 0);
  const showExpansion = !isEditMode && shouldShowExpansionIcon(effectiveDescription);
  const truncatedDescription = effectiveDescription.length > 100
    ? effectiveDescription.substring(0, 100) + '...'
    : effectiveDescription;

  const { hoursToComplete } = useFeatureFlags();
  const showHoursToComplete = hoursToComplete && orgSettings?.showHoursToComplete === true;

  // Budget context behind the read-only Hours to Complete tooltip.
  const budgetContext = useBudgetContext({
    projectId: effectiveProjectId,
    employeeId,
    screenMode,
    projectCode,
    costCentreName: costCentre?.name ?? entry.costCentreName,
    enabled: Boolean(showHoursToComplete && effectiveProjectId),
  });

  const effectiveDailyNotes: Partial<Record<DayKey, string>> = entryEdits.dailyNotes ?? entry.dailyNotes ?? {};

  // Check if this is an absence entry — always locked from editing
  const isAbsence = entry.sourceIntegration === 'people-hr' || entry.isReadOnly === true;

  // Whether this row is editable
  const isRowEditable = isEditMode && !isAbsence;

  // Calculate variance for scheduled bookings
  const variance = entry.isFromSchedule && !entry.isDismissed
    ? calculateEntryVariance(entry)
    : null;

  // Columns spanned by the full-width sub-rows (variance, dismissal, expansion)
  const fullColSpan =
    (orgSettings?.showRole ? 4 : 3) +
    7 +
    (overtimeEnabled ? 1 : 0) +
    (showHoursToComplete ? 1 : 0) +
    1;

  const bookingDetails: BookingDetails = {
    ...entry,
    projectName: project?.name || '',
    costCentreName: costCentre?.name || '',
    scheduledHours: entry.scheduledHours ?? NO_SCHEDULED_HOURS,
  };

  return (
    <React.Fragment>
      <tr className={`border-b transition-colors ${isAbsence ? 'bg-purple-50/30' : ''} ${isRowEditable ? 'bg-amber-50/30 dark:bg-amber-950/10' : 'hover:bg-muted/30'}`}>
        {/* Project cell */}
        <td className="p-2">
          {isRowEditable ? (
            <div className="w-[220px]">
              <ProjectSelector
                projects={projectOptions}
                value={effectiveProjectId}
                onChange={(val) => {
                  const selected = projectOptions.find(p => p.id === val);
                  handleEntryProjectChange(entry.id, val, selected && { name: selected.name, code: selected.code });
                }}
                showFavourites={false}
                placeholder="Select project..."
                companyId={currentCompanyId}
                fallbackName={entry.projectName}
                fallbackCode={entry.projectCode}
              />
            </div>
          ) : (
              <div className="flex flex-col gap-1">
                <div className="text-xs truncate max-w-[200px]">
                  {project ? `${project.name} (${project.code})` : entry.projectName ? `${entry.projectName}${entry.projectCode ? ` (${entry.projectCode})` : ''}` : '-'}
                </div>
                {isAbsence && <AbsenceIndicator />}
              </div>
          )}
        </td>
        {/* Cost Centre cell */}
        <td className="p-2">
          {isRowEditable ? (
            <div className="w-[200px]">
              <CostCentreSelector
                costCentres={entryCostCentres}
                value={effectiveCostCentreId}
                onChange={(val) => {
                  const selected = entryCostCentres.find(cc => cc.id === val);
                  handleEntryCostCentreChange(entry.id, val, selected && { name: selected.name, code: selected.code });
                }}
                disabled={entryCostCentres.length === 0}
                showFavourites={false}
                placeholder="Select cost centre..."
                fallbackName={effectiveCostCentreId ? entry.costCentreName : undefined}
                fallbackCode={effectiveCostCentreId ? entry.costCentreCode : undefined}
              />
            </div>
          ) : (
            <div className="text-xs truncate max-w-[150px]">
              {costCentre ? `${costCentre.name} (${costCentre.code})` : entry.costCentreName ? `${entry.costCentreName}${entry.costCentreCode ? ` (${entry.costCentreCode})` : ''}` : '-'}
            </div>
          )}
        </td>
        {/* Role cell */}
        {orgSettings?.showRole && (
          <td className="p-2">
            {isRowEditable ? (
              <div className="w-[160px]">
                <RoleSelector
                  value={effectiveRoleId}
                  onChange={(roleId, roleName) => handleEntryRoleChange(entry.id, roleId, roleName)}
                  disabled={isAbsence}
                  required={orgSettings?.requireRole}
                  screenMode={screenMode}
                  displayName={effectiveRoleName}
                />
              </div>
            ) : (
              <div className="text-xs truncate max-w-[150px]">
                {effectiveRoleName ?? '-'}
              </div>
            )}
          </td>
        )}
        {/* Description cell */}
        {!isDaily && <td className="p-2">
          {isRowEditable ? (
            <Input
              value={effectiveDescription}
              onChange={(e) => handleEntryFieldChange(entry.id, 'description', e.target.value)}
              className="h-7 text-xs w-[220px]"
              placeholder="Description..."
            />
          ) : (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2 max-w-[280px]">
                {showExpansion && (
                  <button
                    onClick={() => toggleEntryExpansion(entry.id)}
                    className="flex-shrink-0 hover:bg-muted rounded p-0.5 transition-colors"
                    aria-label={isExpanded ? 'Collapse description' : 'Expand description'}
                    aria-expanded={isExpanded}
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                  </button>
                )}
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="text-xs flex-1 min-w-0">
                        {!isExpanded && showExpansion ? (
                          <span className="line-clamp-1">{truncatedDescription}</span>
                        ) : (
                          <span className={showExpansion ? '' : 'line-clamp-2'}>{effectiveDescription || '-'}</span>
                        )}
                      </div>
                    </TooltipTrigger>
                    {effectiveDescription && effectiveDescription.length > 50 && (
                      <TooltipContent side="top" className="max-w-md">
                        <p className="text-xs whitespace-pre-wrap">{effectiveDescription}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          )}
        </td>}
        {/* Notes cell — daily mode only */}
        {isDaily && (() => {
          const daysWithNotes = days.filter(d => effectiveDailyNotes[d as DayKey]?.trim());
          const hasDailyNotes = daysWithNotes.length > 0;
          return (
            <td className="p-2 max-w-[200px]">
              {!hasDailyNotes && !isRowEditable ? (
                <span className="text-xs text-muted-foreground">—</span>
              ) : (
                <button
                  onClick={() => toggleEntryExpansion(entry.id)}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {isExpanded
                    ? <ChevronDown className="h-3.5 w-3.5 flex-shrink-0" />
                    : <ChevronRight className="h-3.5 w-3.5 flex-shrink-0" />
                  }
                  <span>
                    {hasDailyNotes
                      ? `${daysWithNotes.length} day${daysWithNotes.length !== 1 ? 's' : ''} with notes`
                      : 'Add notes'}
                  </span>
                </button>
              )}
            </td>
          );
        })()}
        {/* Hours cells — full Excel-like inputs matching TimesheetGrid */}
        {days.map(day => {
          const cellKey = getHoursCellKey(entry.id, day);
          const isEditing = editingHours[cellKey] !== undefined;
          const displayValue = isEditing
            ? editingHours[cellKey]!
            : effectiveHours[day] === 0
              ? ''
              : effectiveHours[day].toFixed(2);
          const weekend = isWeekendDay(day);
          return (
            <td key={day} className={cn('p-2 text-center', weekend && 'bg-muted/30')}>
              {isRowEditable ? (
                <Input
                  type="text"
                  inputMode="decimal"
                  value={displayValue}
                  placeholder="0"
                  onChange={(e) => {
                    setEditingHours(prev => ({ ...prev, [cellKey]: e.target.value }));
                  }}
                  onFocus={(e) => e.target.select()}
                  onBlur={(e) => {
                    const raw = e.target.value;
                    let hours = raw === '' ? 0 : parseFloat(raw);
                    if (isNaN(hours)) hours = 0;
                    // Apply time rounding
                    const timeRounding = orgSettings?.timeRounding ?? 'none';
                    let step = 0;
                    if (timeRounding === '15min') step = 0.25;
                    else if (timeRounding === '30min') step = 0.5;
                    else if (timeRounding === '1hour') step = 1;
                    if (step > 0) hours = Math.round(hours / step) * step;
                    // Enforce minimum entry duration
                    const minSetting = orgSettings?.minimumEntryDuration ?? '6min';
                    let min = 0.1;
                    if (minSetting === '15min') min = 0.25;
                    else if (minSetting === '30min') min = 0.5;
                    else if (minSetting === '1hour') min = 1;
                    if (hours !== 0 && Math.abs(hours) < min) {
                      hours = hours < 0 ? -min : min;
                    }
                    // Clamp based on allowNegativeHours
                    const allowNeg = orgSettings?.allowNegativeHours ?? false;
                    const clamped = allowNeg
                      ? Math.max(-24, Math.min(24, hours))
                      : Math.max(0, Math.min(24, hours));
                    handleEntryHoursChange(entry.id, day, String(clamped));
                    setEditingHours(prev => {
                      const next = { ...prev };
                      delete next[cellKey];
                      return next;
                    });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.currentTarget.blur();
                    }
                    if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      const current = parseFloat(displayValue) || 0;
                      setEditingHours(prev => ({ ...prev, [cellKey]: Math.min(24, current + 0.5).toFixed(1) }));
                    }
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      const current = parseFloat(displayValue) || 0;
                      setEditingHours(prev => ({ ...prev, [cellKey]: Math.max(0, current - 0.5).toFixed(1) }));
                    }
                  }}
                  className={cn(
                    'text-center text-sm h-9 px-1 sm:px-2',
                    '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                    effectiveHours[day] !== 0 ? 'text-foreground' : 'text-muted-foreground/40',
                    isAbsence && 'bg-muted/50 cursor-not-allowed',
                    !isAbsence && 'hover:border-primary/50 hover:bg-accent/30 transition-colors cursor-text',
                    isAbsence && effectiveHours[day] !== 0 && 'bg-purple-50/40 border-purple-200'
                  )}
                  disabled={isAbsence}
                />
              ) : (
                weekend && effectiveHours[day] !== 0 ? (
                  <span className="text-orange-600 font-semibold text-sm">
                    {effectiveHours[day].toFixed(2)}
                  </span>
                ) : (
                  <span className="text-sm">
                    {effectiveHours[day] !== 0 ? effectiveHours[day].toFixed(2) : '-'}
                  </span>
                )
              )}
            </td>
          );
        })}
        {overtimeEnabled && (
          <td className="p-2">
            {entry.overtimeTypeId != null ? (
              <OvertimeTypeBadge
                name={entry.overtimeTypeName ?? String(entry.overtimeTypeId)}
                colour={overtimeTypes.find(t => t.id === entry.overtimeTypeId)?.badgeColour ?? '#6b7280'}
              />
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            )}
          </td>
        )}

        {/* ── Hours to Complete cell — read-only, reuses the same component as
             the entry form so the tooltip (budget context) and visual style
             are identical. isReadOnly suppresses all editing behaviour. */}
        {showHoursToComplete && (
          <RowHoursToCompleteCell
            entry={entry}
            index={entry.id}
            isReadOnly
            budgetContext={budgetContext}
            onUpdateEntry={() => {}}
          />
        )}

        <td className="p-2 text-center font-semibold">{total.toFixed(2)}</td>
      </tr>

      {/* Variance Display for Scheduled Bookings */}
      {entry.isFromSchedule && !entry.isDismissed && variance && Math.abs(variance.percentage) > 10 && (
        <tr className="border-b bg-blue-50/50 dark:bg-blue-950/20">
          <td colSpan={fullColSpan} className="p-2">
            <div className="flex items-center gap-3 text-xs">
              <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">
                <Calendar className="h-3 w-3 mr-1 text-amber-500" />
                Scheduled Booking
              </Badge>
              <span className="text-muted-foreground">
                Scheduled: {variance.scheduled}h
              </span>
              <span className="text-muted-foreground">|</span>
              <span className="text-muted-foreground">
                Logged: {variance.actual}h
              </span>
              <span className="text-muted-foreground">|</span>
              <Badge variant={getVarianceBadgeVariant(variance.severity)}>
                {variance.variance > 0 ? '+' : ''}{variance.variance}h ({variance.variance > 0 ? '+' : ''}{variance.percentage}%)
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 ml-auto"
                onClick={() => onViewBookingDetails(bookingDetails)}
              >
                <Eye className="h-3 w-3 mr-1" />
                Details
              </Button>
            </div>
          </td>
        </tr>
      )}

      {/* Dismissal Display for Dismissed Bookings */}
      {entry.isDismissed && (
        <tr className="border-b bg-orange-50/50 dark:bg-orange-950/20">
          <td colSpan={fullColSpan} className="p-2">
            <div className="flex items-center gap-3 text-xs">
              <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300">
                <Ban className="h-3 w-3 mr-1 text-muted-foreground" />
                Dismissed Booking
              </Badge>
              <span className="text-muted-foreground">
                {entry.dismissalReason ? `"${entry.dismissalReason}"` : 'No reason provided'}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 ml-auto"
                onClick={() => onViewBookingDetails(bookingDetails)}
              >
                <Eye className="h-3 w-3 mr-1" />
                Details
              </Button>
            </div>
          </td>
        </tr>
      )}

      {!isDaily && isExpanded && showExpansion && (
        <tr className="border-b bg-muted/20">
          <td colSpan={fullColSpan} className="p-3">
            <div className="text-xs">
              <div className="font-medium text-muted-foreground mb-1">Full Description:</div>
              <div className="text-foreground whitespace-pre-wrap bg-background p-3 rounded border">
                {entry.description}
              </div>
            </div>
          </td>
        </tr>
      )}
      {isDaily && isExpanded && (() => {
        if (isRowEditable) {
          // Edit mode — inline inputs for every day with hours
          const daysWithHours = days.filter(d => (effectiveHours[d as DayKey] ?? 0) > 0);
          if (daysWithHours.length === 0) return null;
          return (
            <tr className="border-b bg-amber-50/20 dark:bg-amber-950/10">
              <td colSpan={fullColSpan} className="px-4 py-2">
                <div className="text-xs space-y-1">
                  {daysWithHours.map(d => (
                    <div key={d} className="flex items-center gap-3">
                      <span className="font-medium text-muted-foreground w-7 flex-shrink-0 capitalize">{d.slice(0, 3)}</span>
                      <Input
                        value={effectiveDailyNotes[d as DayKey] ?? ''}
                        onChange={e => handleDailyNoteChange(entry.id, d as DayKey, e.target.value)}
                        className="h-6 text-xs max-w-sm"
                        placeholder="Add a note for this day..."
                      />
                    </div>
                  ))}
                </div>
              </td>
            </tr>
          );
        }
        // Read-only mode — show days that have notes
        const daysWithNotes = days.filter(d => effectiveDailyNotes[d as DayKey]?.trim());
        if (daysWithNotes.length === 0) return null;
        return (
          <tr className="border-b bg-muted/20">
            <td colSpan={fullColSpan} className="px-4 py-2">
              <div className="text-xs space-y-1">
                {daysWithNotes.map(d => (
                  <div key={d} className="flex gap-3">
                    <span className="font-medium text-muted-foreground w-7 flex-shrink-0 capitalize">{d.slice(0, 3)}</span>
                    <span className="text-foreground">{effectiveDailyNotes[d as DayKey]}</span>
                  </div>
                ))}
              </div>
            </td>
          </tr>
        );
      })()}
    </React.Fragment>
  );
}
